import { ActionIcon, Group, Tooltip } from "@mantine/core";
import {
	IconCircleCheck,
	IconCircleX,
	IconShield,
	IconTrash,
} from "@tabler/icons-react";

/**
 * Tiga tombol aksi satu baris pengguna, dipakai tampilan kartu (mobile) dan
 * tabel (desktop) supaya aturan "boleh/tidak"-nya tidak ditulis dua kali.
 *
 * Akun superadmin (email terdaftar di `ADMIN_EMAIL`) dilindungi: admin biasa
 * tidak boleh mengubah role maupun verifikasinya, dan penghapusannya ditutup
 * untuk SIAPA PUN — termasuk sesama superadmin — karena menghapus akunnya hanya
 * menghancurkan data tanpa mencabut wewenang, yang sumbernya tetap `ADMIN_EMAIL`.
 *
 * Ini hanya lapis tampilan. Penolakan yang sebenarnya ada di server
 * (`src/api/admin/superadmin-target.ts`); mematikan tombol saja tidak mengunci
 * apa pun.
 */

export interface UserRowActionsProps {
	/** Baris ini adalah akun yang sedang login. */
	isSelf: boolean;
	/** Baris ini superadmin. */
	isSuperAdmin: boolean;
	/** Yang sedang melihat adalah superadmin. */
	viewerIsSuperAdmin: boolean;
	emailVerified: boolean;
	size: "md" | "lg";
	iconSize: number;
	onRole: () => void;
	onVerify: () => void;
	onDelete: () => void;
}

export function UserRowActions({
	isSelf,
	isSuperAdmin,
	viewerIsSuperAdmin,
	emailVerified,
	size,
	iconSize,
	onRole,
	onVerify,
	onDelete,
}: UserRowActionsProps) {
	/** Superadmin yang dilihat admin biasa: di luar jangkauannya. */
	const locked = isSuperAdmin && !viewerIsSuperAdmin;

	const roleDisabled = isSelf || locked;
	const verifyDisabled = locked;
	const deleteDisabled = isSelf || isSuperAdmin;

	const roleLabel = isSelf
		? "Tidak dapat mengubah role sendiri"
		: locked
			? "Akun superadmin — di luar jangkauan admin"
			: "Ubah role";

	const verifyLabel = locked
		? "Akun superadmin — di luar jangkauan admin"
		: emailVerified
			? "Batalkan verifikasi"
			: "Verifikasi manual";

	const deleteLabel = isSelf
		? "Tidak dapat menghapus akun sendiri"
		: isSuperAdmin
			? "Akun superadmin tidak bisa dihapus dari sini"
			: "Hapus pengguna";

	return (
		<Group gap="xs" wrap="nowrap" flex="none">
			<Tooltip label={roleLabel}>
				<ActionIcon
					variant="light"
					color="orange"
					size={size}
					disabled={roleDisabled}
					onClick={onRole}
					aria-label={roleLabel}
				>
					<IconShield size={iconSize} />
				</ActionIcon>
			</Tooltip>
			<Tooltip label={verifyLabel}>
				<ActionIcon
					variant="light"
					color={emailVerified ? "gray" : "green"}
					size={size}
					disabled={verifyDisabled}
					onClick={onVerify}
					aria-label={verifyLabel}
				>
					{emailVerified ? (
						<IconCircleX size={iconSize} />
					) : (
						<IconCircleCheck size={iconSize} />
					)}
				</ActionIcon>
			</Tooltip>
			<Tooltip label={deleteLabel}>
				<ActionIcon
					variant="light"
					color="red"
					size={size}
					disabled={deleteDisabled}
					onClick={onDelete}
					aria-label={deleteLabel}
				>
					<IconTrash size={iconSize} />
				</ActionIcon>
			</Tooltip>
		</Group>
	);
}
